import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { Loading } from '../components/Loading';
import { EmptyState } from '../components/EmptyState';

const POST_TYPES = [
  { id: 'promocao', label: 'Promoção', emoji: '🔥' },
  { id: 'novidade', label: 'Novidade', emoji: '✨' },
  { id: 'evento', label: 'Evento', emoji: '🎉' },
  { id: 'aviso', label: 'Aviso', emoji: '📢' },
  { id: 'emprego', label: 'Emprego', emoji: '💼' },
];

// LE-037 (CRUD): datas AAAA-MM-DD locais para os inputs <input type="date">
const todayISO = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};
const plusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};
const sliceDate = (v) => (v ? String(v).slice(0, 10) : '');

// Status derivado no cliente (is_active + datas + cancelled_at) — mesma regra
// do popup do mapa. Atenção: o backend devolve DATE como datetime ISO (ex.:
// 2026-08-09T00:00:00.000Z) — por isso as comparações usam sliceDate()
// (date-only); comparar a string ISO com a data pura faria o valid_from sempre
// parecer no futuro (status errado). LE-037 (reativar/renovar): cancelled_at
// distingue Cancelada (pelo comerciante — pode reativar) de Desativada (pela
// moderação — sem ações).
function promoStatus(p) {
  if (!p.is_active) {
    return p.cancelled_at
      ? { label: 'Cancelada', color: '#dc2626' }
      : { label: 'Desativada', color: 'var(--nc-text-secondary)' };
  }
  const today = todayISO();
  const from = sliceDate(p.valid_from);
  const until = sliceDate(p.valid_until);
  if (from && from > today) return { label: 'Agendada', color: 'var(--nc-boost)' };
  if (until && until < today) return { label: 'Expirada', color: 'var(--nc-text-muted)' };
  return { label: 'Ativa', color: 'var(--nc-success-text)' };
}

const emptyForm = { type: 'promocao', title: '', content: '', valid_from: todayISO(), valid_until: plusDays(30) };

export function MerchantPostsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const businessId = searchParams.get('business_id');
  const [businesses, setBusinesses] = useState([]);
  const [selected, setSelected] = useState(businessId || '');
  const [posts, setPosts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promosLoading, setPromosLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingPromo, setEditingPromo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  // Carrega a lista de estabelecimentos do comerciante (para o seletor)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/merchant/businesses');
        const list = data.data || [];
        setBusinesses(list);
        if (!selected && list.length > 0) setSelected(String(list[0].id));
      } catch {
        // ignora
      }
    })();
  }, []); // eslint-disable-line

  const fetchPosts = useCallback(async () => {
    if (!selected) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      // O backend já filtra por business_id (LE-020 S8) — sem filtro extra no cliente
      const { data } = await api.get('/feed', { params: { business_id: selected, limit: 50 } });
      setPosts(data.posts || data.data || []);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erro ao carregar publicações');
    } finally {
      setLoading(false);
    }
  }, [selected]);

  // LE-037 (CRUD): lista de promoções do comércio selecionado
  const fetchPromotions = useCallback(async () => {
    if (!selected) { setPromosLoading(false); return; }
    setPromosLoading(true);
    try {
      const { data } = await api.get(`/merchant/businesses/${selected}/promotions`);
      setPromotions(data.data || []);
    } catch {
      setPromotions([]);
    } finally {
      setPromosLoading(false);
    }
  }, [selected]);

  useEffect(() => {
    if (selected) {
      fetchPosts();
      fetchPromotions();
    }
  }, [selected, fetchPosts, fetchPromotions]);

  const refreshAll = useCallback(() => {
    fetchPosts();
    fetchPromotions();
  }, [fetchPosts, fetchPromotions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSaving(true);
    setMessage(null);
    try {
      const isPromo = form.type === 'promocao';
      // LE-037 (CRUD): a promoção leva a validade escolhida (inputs visíveis só
      // para o tipo Promoção); os demais tipos ignoram os campos no backend.
      const body = isPromo
        ? { ...form }
        : { type: form.type, title: form.title, content: form.content };
      await api.post(`/merchant/businesses/${selected}/posts`, body);
      setForm({ ...emptyForm, type: form.type });
      setMessage({
        type: 'ok',
        text: isPromo
          ? 'Promoção publicada! Ela aparece no feed da cidade e no card do mapa (comércios impulsionados).'
          : 'Publicação criada! Ela aparece no feed da cidade.',
      });
      refreshAll();
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao publicar.' });
    } finally {
      setSaving(false);
    }
  };

  // LE-037 (CRUD): começa a edição de uma promoção (preenche o formulário)
  const handleEditPromo = (p) => {
    setEditingPromo(p);
    setForm({
      type: 'promocao',
      title: p.title || '',
      content: p.description || '',
      valid_from: sliceDate(p.valid_from) || todayISO(),
      valid_until: sliceDate(p.valid_until) || plusDays(30),
    });
    setMessage(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingPromo(null);
    setForm({ ...emptyForm, type: 'promocao' });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingPromo) return;
    setSaving(true);
    setMessage(null);
    try {
      await api.patch(`/merchant/promotions/${editingPromo.id}`, {
        title: form.title,
        description: form.content,
        valid_from: form.valid_from,
        valid_until: form.valid_until,
      });
      setEditingPromo(null);
      setForm({ ...emptyForm, type: 'promocao' });
      setMessage({ type: 'ok', text: 'Promoção atualizada! As alterações aparecem no feed e no mapa.' });
      refreshAll();
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao atualizar a promoção.' });
    } finally {
      setSaving(false);
    }
  };

  // LE-037 (CRUD): cancela a promoção (some do feed e do popup do mapa)
  const handleCancelPromo = async (p) => {
    if (!window.confirm(`Cancelar a promoção "${p.title}"? Ela sairá do feed e do mapa.`)) return;
    setMessage(null);
    try {
      await api.delete(`/merchant/promotions/${p.id}`);
      setMessage({ type: 'ok', text: 'Promoção cancelada! Ela saiu do feed e do mapa.' });
      refreshAll();
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao cancelar a promoção.' });
    }
  };

  // LE-037 (reativar): desfaz o cancelamento feito pelo próprio comerciante —
  // a promoção volta ao feed e ao popup do mapa. Promoção desativada pela
  // moderação do admin não tem o botão (e o backend responde 409 se tentar).
  const handleReactivatePromo = async (p) => {
    if (!window.confirm(`Reativar a promoção "${p.title}"? Ela voltará ao feed e ao mapa.`)) return;
    setMessage(null);
    try {
      await api.post(`/merchant/promotions/${p.id}/reactivate`);
      setMessage({ type: 'ok', text: 'Promoção reativada! Ela voltou ao feed e ao mapa.' });
      refreshAll();
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao reativar a promoção.' });
    }
  };

  // LE-037 (renovar/duplicar): cria uma cópia NOVA da promoção (validade padrão
  // de 30 dias, ajustável depois pelo Editar). A original permanece intacta.
  const renewPromotion = useCallback(async (promo) => {
    await api.post(`/merchant/promotions/${promo.id}/renew`);
    setMessage({ type: 'ok', text: 'Promoção renovada! Uma nova versão foi publicada no feed e no mapa.' });
    refreshAll();
  }, [refreshAll]);

  const handleRenewPromo = async (p) => {
    if (!window.confirm(`Renovar a promoção "${p.title}"? Uma nova versão com validade de 30 dias será criada (a atual permanece).`)) return;
    setMessage(null);
    try {
      await renewPromotion(p);
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao renovar a promoção.' });
    }
  };

  // LE-037 (renovar no card do mapa): o atalho do popup abre esta página em
  // MODO RENOVAR (?renew=<promoId>). A página pré-cadencia o MESMO diálogo de
  // confirmação da lista e dispara o POST /renew ao carregar. O parâmetro é
  // removido da URL assim que tratado — refresh/voltar não re-disparam.
  const renewHandledRef = useRef(false);
  useEffect(() => {
    const renewId = searchParams.get('renew');
    if (!renewId || renewHandledRef.current || promosLoading) return;
    renewHandledRef.current = true;

    const cleanup = () => {
      const params = new URLSearchParams(searchParams);
      params.delete('renew');
      setSearchParams(params, { replace: true });
    };

    const promo = promotions.find(p => String(p.id) === renewId);
    if (!promo) {
      cleanup();
      setMessage({ type: 'err', text: 'Promoção não encontrada para renovar.' });
      return;
    }

    cleanup();
    const confirmed = window.confirm(
      `Renovar a promoção "${promo.title}"? Uma nova versão com validade de 30 dias será criada (a atual permanece).`
    );
    if (!confirmed) {
      setMessage({ type: 'ok', text: 'Renovação cancelada. A promoção atual permanece.' });
      return;
    }

    setMessage(null);
    renewPromotion(promo).catch((err) => {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao renovar a promoção.' });
    });
  }, [searchParams, setSearchParams, promotions, promosLoading, renewPromotion]);

  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid var(--nc-border)',
    fontSize: 14, marginBottom: 12, outline: 'none', background: 'var(--nc-surface)', color: 'var(--nc-text)', boxSizing: 'border-box'
  };

  const postTypeLabel = (t) => POST_TYPES.find(x => x.id === t)?.label || t;

  return (
    <div className="nc-page">
      <div className="nc-pagehead">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <button onClick={() => navigate('/comerciante/estabelecimentos')} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="nc-page-title">Publicações</h1>
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {businesses.length === 0 ? (
          <EmptyState
            icon="🏪"
            title="Nenhum estabelecimento"
            description="Você precisa cadastrar um comércio antes de publicar."
            action={<button className="nc-btn-primary" onClick={() => navigate('/comerciante/solicitar')}>Cadastrar comércio</button>}
          />
        ) : (
          <>
            <label className="nc-hint" style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
              Estabelecimento
            </label>
            <select value={selected} onChange={(e) => { setSelected(e.target.value); handleCancelEdit(); }} style={inputStyle} aria-label="Estabelecimento">
              {businesses.map(b => (
                <option key={b.id} value={b.id}>{b.name}{!b.is_approved ? ' (pendente)' : ''}</option>
              ))}
            </select>

            {message && (
              <div role="status" style={{
                padding: '10px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13,
                background: message.type === 'ok' ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${message.type === 'ok' ? '#bbf7d0' : '#fecaca'}`,
                color: message.type === 'ok' ? '#15803d' : '#b91c1c'
              }}>
                {message.text}
              </div>
            )}

            {/* Formulário de nova publicação / edição de promoção */}
            <form
              onSubmit={editingPromo ? handleSaveEdit : handleSubmit}
              style={{ background: 'var(--nc-surface)', borderRadius: 14, border: '1px solid var(--nc-border)', padding: 16, marginBottom: 18 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)' }}>
                  {editingPromo ? 'Editar promoção' : 'Nova publicação'}
                </div>
                {editingPromo && (
                  <button type="button" onClick={handleCancelEdit} className="nc-hint" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                    Cancelar edição
                  </button>
                )}
              </div>
              <label className="nc-hint" style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Tipo</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                {POST_TYPES.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    disabled={!!editingPromo}
                    onClick={() => setForm(prev => ({ ...emptyForm, type: t.id }))}
                    aria-pressed={form.type === t.id}
                    style={{
                      padding: '6px 12px', borderRadius: 20, border: 'none', cursor: editingPromo ? 'not-allowed' : 'pointer',
                      background: form.type === t.id ? '#0d5c3b' : '#f3f4f6',
                      color: form.type === t.id ? '#fff' : '#6b7280', fontSize: 12, fontWeight: 500,
                      opacity: editingPromo && form.type !== t.id ? 0.4 : 1
                    }}
                  >
                    {t.emoji} {t.label}
                  </button>
                ))}
              </div>
              <label className="nc-hint" style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Título</label>
              <input type="text" value={form.title} onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))} placeholder="Ex.: Picanha em promoção!" style={inputStyle} aria-label="Título" />
              <label className="nc-hint" style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Conteúdo</label>
              <textarea value={form.content} onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))} rows={3} required placeholder="Descreva sua publicação..." style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} aria-label="Conteúdo" />
              {/* LE-037 (CRUD): validade escolhida pelo comerciante (só para promoções) */}
              {form.type === 'promocao' && (
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label className="nc-hint" style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Início</label>
                    <input type="date" value={form.valid_from} onChange={(e) => setForm(prev => ({ ...prev, valid_from: e.target.value }))} style={inputStyle} aria-label="Início da promoção" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="nc-hint" style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Até</label>
                    <input type="date" value={form.valid_until} onChange={(e) => setForm(prev => ({ ...prev, valid_until: e.target.value }))} style={inputStyle} aria-label="Fim da promoção" />
                  </div>
                </div>
              )}
              <button type="submit" disabled={saving} className="nc-btn-primary" style={{ width: '100%' }}>
                {saving ? (editingPromo ? 'Salvando...' : 'Publicando...') : (editingPromo ? 'Salvar alterações' : 'Publicar')}
              </button>
            </form>

            {/* LE-037 (CRUD): lista de promoções do comércio */}
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
              Promoções do comércio
            </div>
            {promosLoading ? <Loading /> : promotions.length === 0 ? (
              <div className="nc-body-sm" style={{ background: 'var(--nc-surface)', borderRadius: 10, border: '1px solid var(--nc-border)', padding: 14, marginBottom: 18 }}>
                Nenhuma promoção criada ainda. Publique com o tipo <b>Promoção</b> acima para aparecer também no mapa.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
                {promotions.map(p => {
                  const st = promoStatus(p);
                  return (
                    <div key={p.id} style={{ background: 'var(--nc-surface)', borderRadius: 10, border: '1px solid var(--nc-border)', padding: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--nc-on-primary)', background: 'var(--nc-danger)', padding: '2px 8px', borderRadius: 10 }}>PROMO</span>
                        <span style={{ fontSize: 10, fontWeight: 600, color: st.color, padding: '2px 8px', borderRadius: 10, background: 'var(--nc-surface-alt)' }}>
                          {st.label}
                        </span>
                        <span className="nc-caption" style={{ marginLeft: 'auto' }}>
                          {sliceDate(p.valid_from)} → {sliceDate(p.valid_until)}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text)' }}>{p.title}</div>
                      {p.description && <div style={{ fontSize: 13, color: 'var(--nc-text-dim)' }}>{p.description}</div>}
                      {/* Ações por estado (LE-037 reativar/renovar):
                          Ativa/Agendada → Editar | Cancelar | Renovar
                          Cancelada (pelo comerciante) → Reativar | Renovar
                          Expirada → Renovar
                          Desativada (moderação) → nenhuma (não pode desfazer admin) */}
                      {(st.label === 'Ativa' || st.label === 'Agendada') && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button type="button" aria-label={`Editar promoção ${p.title}`} onClick={() => handleEditPromo(p)} style={{
                            flex: 1, padding: '7px 0', borderRadius: 8, border: '1px solid var(--nc-border)',
                            background: 'var(--nc-surface)', color: 'var(--nc-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer'
                          }}>
                            Editar
                          </button>
                          <button type="button" aria-label={`Cancelar promoção ${p.title}`} onClick={() => handleCancelPromo(p)} style={{
                            flex: 1, padding: '7px 0', borderRadius: 8, border: 'none',
                            background: 'var(--nc-danger-soft)', color: 'var(--nc-danger-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer'
                          }}>
                            Cancelar promoção
                          </button>
                          <button type="button" aria-label={`Renovar promoção ${p.title}`} onClick={() => handleRenewPromo(p)} style={{
                            flex: 1, padding: '7px 0', borderRadius: 8, border: 'none',
                            background: '#eff6ff', color: '#1d4ed8', fontSize: 12, fontWeight: 600, cursor: 'pointer'
                          }}>
                            Renovar
                          </button>
                        </div>
                      )}
                      {st.label === 'Cancelada' && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button type="button" aria-label={`Reativar promoção ${p.title}`} onClick={() => handleReactivatePromo(p)} style={{
                            flex: 1, padding: '7px 0', borderRadius: 8, border: 'none',
                            background: 'var(--nc-success-soft)', color: 'var(--nc-success-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer'
                          }}>
                            Reativar
                          </button>
                          <button type="button" aria-label={`Renovar promoção ${p.title}`} onClick={() => handleRenewPromo(p)} style={{
                            flex: 1, padding: '7px 0', borderRadius: 8, border: 'none',
                            background: '#eff6ff', color: '#1d4ed8', fontSize: 12, fontWeight: 600, cursor: 'pointer'
                          }}>
                            Renovar
                          </button>
                        </div>
                      )}
                      {st.label === 'Expirada' && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button type="button" aria-label={`Renovar promoção ${p.title}`} onClick={() => handleRenewPromo(p)} style={{
                            flex: 1, padding: '7px 0', borderRadius: 8, border: 'none',
                            background: '#eff6ff', color: '#1d4ed8', fontSize: 12, fontWeight: 600, cursor: 'pointer'
                          }}>
                            Renovar
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Lista de publicações */}
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
              Publicações do feed
            </div>
            {loading ? <Loading /> : error ? (
              <div role="alert" style={{ padding: 12, borderRadius: 10, background: 'var(--nc-danger-soft)', border: '1px solid var(--nc-danger-border)', color: 'var(--nc-danger-text)', fontSize: 13 }}>
                {error}
              </div>
            ) : posts.length === 0 ? (
              <EmptyState icon="📭" title="Sem publicações ainda" description="Crie sua primeira publicação acima." />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {posts.map(p => (
                  <div key={p.id} style={{ background: 'var(--nc-surface)', borderRadius: 10, border: '1px solid var(--nc-border)', padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--nc-on-primary)', background: 'var(--nc-primary)', padding: '2px 8px', borderRadius: 10 }}>
                        {postTypeLabel(p.type)}
                      </span>
                      <span className="nc-caption" style={{ marginLeft: 'auto' }}>
                        {new Date(p.created_at).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    {p.title && <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text)' }}>{p.title}</div>}
                    <div style={{ fontSize: 13, color: 'var(--nc-text-dim)' }}>{p.content}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
